import { useMemo, useRef } from 'react';
import { exportDatabase, importDatabase } from '../../lib/storage/db';
import { computeDiagnostic } from '../../lib/progress/diagnostic';
import { puzzleThemesForWeakness } from '../../lib/puzzles/themes';
import type { AppDatabaseExport, CoachingReport, Exercise, ExerciseAttempt, PuzzleAttempt, StoredGame } from '../../types';

export interface ProgressTabProps {
  games: StoredGame[];
  reports: Record<string, CoachingReport>;
  exercises: Exercise[];
  attempts: ExerciseAttempt[];
  puzzleAttempts: PuzzleAttempt[];
  storageError: string | null;
  onDataImported: () => void;
  onTrainWeakness: (puzzleThemes: string[]) => void;
}

interface ThemeStat {
  theme: string;
  total: number;
  autonomous: number;
  hinted: number;
  revealed: number;
  failed: number;
}

function computeThemeStats(exercises: Exercise[], attempts: ExerciseAttempt[]): ThemeStat[] {
  const byTheme = new Map<string, ThemeStat>();
  for (const ex of exercises) {
    const key = ex.theme ?? 'sans thème identifié';
    if (!byTheme.has(key)) {
      byTheme.set(key, { theme: key, total: 0, autonomous: 0, hinted: 0, revealed: 0, failed: 0 });
    }
    const stat = byTheme.get(key)!;
    stat.total += 1;
    const exerciseAttempts = attempts.filter((a) => a.exerciseId === ex.id);
    for (const a of exerciseAttempts) {
      if (a.outcome === 'autonomous_success') stat.autonomous += 1;
      else if (a.outcome === 'hinted_success') stat.hinted += 1;
      else if (a.outcome === 'revealed') stat.revealed += 1;
      else stat.failed += 1;
    }
  }
  return [...byTheme.values()].sort((a, b) => b.total - a.total);
}

export function ProgressTab({
  games,
  reports,
  exercises,
  attempts,
  puzzleAttempts,
  storageError,
  onDataImported,
  onTrainWeakness,
}: ProgressTabProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const themeStats = useMemo(() => computeThemeStats(exercises, attempts), [exercises, attempts]);
  const diagnostic = useMemo(
    () => computeDiagnostic({ games, reports, puzzleAttempts }),
    [games, reports, puzzleAttempts],
  );

  async function handleExport() {
    const data = await exportDatabase();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `magnus-sauvegarde-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text) as AppDatabaseExport;
      if (!data || !Array.isArray(data.games) || typeof data.version !== 'number') {
        alert("Ce fichier ne ressemble pas à une sauvegarde Magnus valide.");
        return;
      }
      await importDatabase(data);
      onDataImported();
      alert('Sauvegarde importée avec succès.');
    } catch (err) {
      alert(`Impossible d'importer ce fichier : ${err instanceof Error ? err.message : 'erreur inconnue'}`);
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <section className="panel">
      <h2>Ma progression</h2>

      {storageError && (
        <p className="error-block" role="alert">
          {storageError}
        </p>
      )}

      <p className="muted">
        Le stockage est propre à ce navigateur (IndexedDB) : il peut être effacé si tu vides les données de
        navigation. Exporte régulièrement une sauvegarde JSON pour la conserver.
      </p>

      <div className="stat-summary">
        <div className="stat-tile">
          <strong>{games.length}</strong>
          <span>partie{games.length > 1 ? 's' : ''} importée{games.length > 1 ? 's' : ''}</span>
        </div>
        <div className="stat-tile">
          <strong>{exercises.length}</strong>
          <span>exercice{exercises.length > 1 ? 's' : ''} généré{exercises.length > 1 ? 's' : ''}</span>
        </div>
        <div className="stat-tile">
          <strong>{attempts.length}</strong>
          <span>tentative{attempts.length > 1 ? 's' : ''} enregistrée{attempts.length > 1 ? 's' : ''}</span>
        </div>
      </div>

      {diagnostic.recentGames.length > 0 && (
        <>
          <h3>Diagnostic</h3>
          <p className="muted small">
            Gaffes et erreurs sur les {diagnostic.recentGames.length} dernière
            {diagnostic.recentGames.length > 1 ? 's' : ''} partie{diagnostic.recentGames.length > 1 ? 's' : ''}{' '}
            analysée{diagnostic.recentGames.length > 1 ? 's' : ''}.
          </p>
          <div className="diag-games" role="img" aria-label="Taux de gaffes et erreurs par partie récente">
            {diagnostic.recentGames.map((g) => (
              <div key={g.gameId} className="diag-bar-col" title={`${g.label} : ${g.mistakes + g.blunders} gaffe(s)/erreur(s) sur ${g.playerMoves} coups`}>
                <div className="diag-bar" style={{ height: `${Math.max(4, Math.min(100, g.mistakeRate * 300))}%` }} />
                <span className="muted small">{g.label}</span>
              </div>
            ))}
          </div>
          {diagnostic.trend && (
            <p className="muted small">
              {diagnostic.trend.direction === 'improving' &&
                'Tendance : moins de gaffes que sur les 10 parties analysées précédentes.'}
              {diagnostic.trend.direction === 'worsening' &&
                'Tendance : plus de gaffes que sur les 10 parties analysées précédentes.'}
              {diagnostic.trend.direction === 'stable' &&
                'Tendance : stable par rapport aux 10 parties analysées précédentes.'}
            </p>
          )}

          {diagnostic.primaryWeakness && (
            <div className="diag-tile">
              <p>
                <strong>Cause n°1 : {diagnostic.primaryWeakness.theme.replace(/_/g, ' ')}</strong> (
                {Math.round(diagnostic.primaryWeakness.share * 100)}% des thèmes détectés)
              </p>
              <p className="muted small">{diagnostic.primaryWeakness.reflex}</p>
              <button
                type="button"
                className="primary-btn"
                onClick={() => onTrainWeakness(puzzleThemesForWeakness([diagnostic.primaryWeakness!.theme]))}
              >
                S'entraîner sur ce thème
              </button>
            </div>
          )}

          {diagnostic.puzzleThemeStats.length > 0 && (
            <table className="diag-puzzle-table">
              <thead>
                <tr>
                  <th>Thème de puzzle</th>
                  <th>Réussite</th>
                </tr>
              </thead>
              <tbody>
                {diagnostic.puzzleThemeStats.map((s) => (
                  <tr key={s.theme}>
                    <td>{s.label}</td>
                    <td>
                      {s.solved}/{s.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {diagnostic.eloSeries.length >= 2 && (
            <>
              <p className="muted small" style={{ marginTop: '0.85rem' }}>
                Elo Chess.com (toutes cadences confondues)
              </p>
              <EloSparkline points={diagnostic.eloSeries} />
            </>
          )}

          <p className="muted small" style={{ marginTop: '0.5rem' }}>
            Estimation basée sur l'analyse à profondeur 12 (balayage) — pas un score officiel.
          </p>
        </>
      )}

      <h3>Thèmes rencontrés</h3>
      {themeStats.length === 0 ? (
        <p className="muted">Pas encore de thème détecté — analyse une partie pour commencer.</p>
      ) : (
        <>
          <div className="theme-bars">
            {themeStats.map((s) => {
              const attempted = s.autonomous + s.hinted + s.revealed;
              const pct = (n: number) => (attempted > 0 ? (n / attempted) * 100 : 0);
              return (
                <div className="theme-bar-row" key={s.theme}>
                  <div className="theme-bar-label">
                    <span>{s.theme.replace(/_/g, ' ')}</span>
                    <span className="muted">{s.total} exercice{s.total > 1 ? 's' : ''}</span>
                  </div>
                  <div className="theme-bar-track" role="img" aria-label={`${s.autonomous} réussis seul, ${s.hinted} avec indice, ${s.revealed} révélés sur ${s.total}`}>
                    {s.autonomous > 0 && (
                      <div className="theme-bar-seg-autonomous" style={{ width: `${pct(s.autonomous)}%` }} />
                    )}
                    {s.hinted > 0 && <div className="theme-bar-seg-hinted" style={{ width: `${pct(s.hinted)}%` }} />}
                    {s.revealed > 0 && (
                      <div className="theme-bar-seg-revealed" style={{ width: `${pct(s.revealed)}%` }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="theme-bar-legend muted small">
            <span>
              <i className="theme-bar-seg-autonomous" /> Seul
            </span>
            <span>
              <i className="theme-bar-seg-hinted" /> Avec indice
            </span>
            <span>
              <i className="theme-bar-seg-revealed" /> Révélé
            </span>
          </div>
        </>
      )}
      <p className="muted small" style={{ marginTop: '0.85rem' }}>
        Ces chiffres sont des comptages bruts de tes tentatives, pas un score de maîtrise inventé.
      </p>

      <h3>Sauvegarde</h3>
      <div className="actions">
        <button type="button" className="primary-btn" onClick={handleExport}>
          Exporter ma sauvegarde (JSON)
        </button>
        <label className="file-btn">
          Importer une sauvegarde JSON
          <input ref={fileRef} type="file" accept="application/json" onChange={handleImportFile} hidden />
        </label>
      </div>
    </section>
  );
}

/** Petite courbe SVG inline — pas de bibliothèque de graphiques pour un seul tracé. */
function EloSparkline({ points }: { points: { date: string; rating: number }[] }) {
  const width = 320;
  const height = 72;
  const pad = 8;
  const ratings = points.map((p) => p.rating);
  const min = Math.min(...ratings);
  const max = Math.max(...ratings);
  const span = Math.max(1, max - min);
  const stepX = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = pad + i * stepX;
    const y = height - pad - ((p.rating - min) / span) * (height - pad * 2);
    return { x, y };
  });
  const polyline = coords.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label={`Évolution de l'Elo Chess.com, de ${ratings[0]} à ${ratings[ratings.length - 1]}`}
    >
      <polyline points={polyline} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={2.5} fill="hsl(var(--primary))" />
      ))}
    </svg>
  );
}
